import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  AccountShell,
  BackLink,
  PageHeader,
  mono,
  serif,
} from "@/components/AccountLayout";
import { sendContactMessage } from "@/lib/contact.functions";
import { getMyAccount } from "@/lib/account.functions";

type Topic = "hello" | "access" | "technical" | "refund" | "other";

const TOPIC_TO_CATEGORY: Record<Topic, string> = {
  hello: "Just saying hello",
  access: "Course access — I can't get in",
  technical: "A lesson isn't working",
  refund: "Refund",
  other: "Something else",
};

const CATEGORIES = [
  TOPIC_TO_CATEGORY.hello,
  TOPIC_TO_CATEGORY.access,
  TOPIC_TO_CATEGORY.technical,
  TOPIC_TO_CATEGORY.refund,
  TOPIC_TO_CATEGORY.other,
];

const VALID_TOPICS: Topic[] = ["hello", "access", "technical", "refund", "other"];

export const Route = createFileRoute("/account_/contact")({
  head: () => {
    const title = "Contact us — WHO KNEW";
    const description =
      "Manage your WHO KNEW account, view your purchase, track your progress, or get in touch.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/account/contact" },
      ],
    };
  },
  validateSearch: (search: Record<string, unknown>): { topic?: Topic } => {
    const t = search.topic;
    if (typeof t === "string" && (VALID_TOPICS as string[]).includes(t)) {
      return { topic: t as Topic };
    }
    return {};
  },
  component: ContactPage,
});

function ContactPage() {
  const search = Route.useSearch();
  const topic = search.topic as Topic | undefined;
  const initialCategory = topic ? TOPIC_TO_CATEGORY[topic] : TOPIC_TO_CATEGORY.hello;
  const send = useServerFn(sendContactMessage);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState(initialCategory);
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (res.error === "no_session") navigate({ to: "/signin" });
          return;
        }
        setEmail(res.email);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const fieldStyle = {
    ...mono,
    fontSize: "14px",
    backgroundColor: "#E8E1D3",
    border: "none",
    color: "#1A1612",
    padding: "14px 16px",
    width: "100%",
    outline: "none",
    borderRadius: 0,
  } as const;

  const labelStyle = {
    ...mono,
    fontSize: "10px",
    color: "#5A534A",
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
    fontWeight: 700,
    marginBottom: "8px",
    display: "block",
  };

  const errorStyle = {
    ...mono,
    fontSize: "11px",
    color: "#8B1E1E",
    marginTop: "6px",
    fontWeight: 700,
    letterSpacing: "0.03em",
  } as const;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setFieldErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await send({
        data: { email, category, message, website },
      });
      if (result.ok) {
        setSent(true);
      } else {
        setFormError(result.error);
      }
    } catch (err) {
      // Validation errors thrown by inputValidator
      const raw = err instanceof Error ? err.message : String(err);
      try {
        // The thrown payload may be wrapped — try to extract JSON
        const match = raw.match(/\{.*\}/s);
        if (match) {
          const parsed = JSON.parse(match[0]) as {
            kind?: string;
            fieldErrors?: Record<string, string>;
          };
          if (parsed.kind === "validation" && parsed.fieldErrors) {
            setFieldErrors(parsed.fieldErrors);
            setFormError("Please check the fields below.");
            return;
          }
        }
      } catch {
        // fall through
      }
      setFormError("Something went wrong sending your note. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AccountShell>
      {sent ? (
        <>
          <PageHeader eyebrow="NOTE SENT" title="We've got it." />
          <p
            className="text-ink"
            style={{ ...serif, fontSize: "18px", marginTop: "32px" }}
          >
            We'll be in touch within 24 hours. Including Sunday.
          </p>
          <p style={{ marginTop: "24px", paddingBottom: "96px" }}>
            <Link
              to="/account"
              className="text-burgundy italic underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
              style={{ ...serif, fontSize: "18px" }}
            >
              Back to your account
            </Link>
          </p>
        </>
      ) : (
        <>
          <BackLink />
          <PageHeader eyebrow="CONTACT US" title="How can we help?" />

          <p
            className="italic"
            style={{ ...serif, fontSize: "18px", color: "#5A534A", marginTop: "24px" }}
          >
            We reply within 24 hours. Including Sunday.
          </p>

          <form onSubmit={handleSubmit} style={{ marginTop: "48px" }} noValidate>
            {/* Honeypot: hidden from real users, attractive to bots */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </label>
            </div>

            <div>
              <label style={labelStyle}>YOUR EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={fieldStyle}
                required
              />
              {fieldErrors.email && <div style={errorStyle}>{fieldErrors.email}</div>}
            </div>

            <div style={{ marginTop: "16px" }}>
              <label style={labelStyle}>WHAT'S THIS ABOUT</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={fieldStyle}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {fieldErrors.category && (
                <div style={errorStyle}>{fieldErrors.category}</div>
              )}
            </div>

            <div style={{ marginTop: "16px" }}>
              <label style={labelStyle}>YOUR NOTE</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's going on."
                rows={5}
                style={{ ...fieldStyle, resize: "vertical" as const }}
                required
              />
              {fieldErrors.message && (
                <div style={errorStyle}>{fieldErrors.message}</div>
              )}
            </div>

            {formError && (
              <div
                role="alert"
                style={{
                  ...mono,
                  fontSize: "12px",
                  color: "#8B1E1E",
                  marginTop: "20px",
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                }}
              >
                {formError}
              </div>
            )}

            <div style={{ marginTop: "24px" }}>
              <button
                type="submit"
                disabled={submitting || !email}
                className="font-bold uppercase tracking-[0.05em] cursor-pointer transition-colors"
                style={{
                  ...mono,
                  fontSize: "12px",
                  color: "#F5EFE2",
                  backgroundColor: submitting ? "#6B1717" : "#8B1E1E",
                  padding: "18px 32px",
                  border: "none",
                  borderRadius: 0,
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = "#6B1717";
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = "#8B1E1E";
                }}
              >
                {submitting ? "SENDING…" : "SEND"}
              </button>
            </div>
          </form>
        </>
      )}

      <div style={{ marginTop: "48px" }}>
        <p
          className="italic"
          style={{ ...serif, fontSize: "14px", color: "#5A534A" }}
        >
          Or, if you prefer the old-fashioned way:{" "}
          <a
            href="mailto:hello@whoknew.guide"
            className="text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
          >
            hello@whoknew.guide
          </a>
        </p>
        <p
          className="italic"
          style={{ ...serif, fontSize: "14px", color: "#5A534A", marginTop: "24px", paddingBottom: "96px" }}
        >
          Refund window is 14 days from purchase. After that, we might still help. Just
          say.
        </p>
      </div>
    </AccountShell>
  );
}
